import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { SUBJ, RIASEC, PARENTF, MAJORS, CAREER_SLUGS, OUTLOOK_SCORE, LANGS } from "../mock/data";
import I18N from "../mock/i18n";
import { BrandmarkSVG, CompassSVG } from "./Compass";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Share2, Copy, Check, LogIn, LayoutDashboard, Smartphone } from "lucide-react";
import PhoneLogin from "./PhoneLogin";

function normalize(major, key, userVec, order, maxVal) {
  const weights = major[key];
  let sumW = 0, sumWU = 0;
  for (let i = 0; i < order.length; i++) {
    const w = weights[i];
    const u = userVec[order[i]] / maxVal;
    sumW += w;
    sumWU += w * u;
  }
  if (sumW === 0) return 0.5;
  return sumWU / sumW;
}

function familyFit(major, selectedSet) {
  const keys = Object.keys(selectedSet).filter((k) => selectedSet[k]);
  if (keys.length === 0) return 0.5;
  if (keys.length === 1 && keys[0] === "undecided") return 0.5;
  if (selectedSet[major.p]) return 1.0;
  return 0.15;
}

function computeResults(state, balance) {
  const wSubject = 0.30, wOutlook = 0.10;
  const wInterest = 0.60 * (1 - balance / 100);
  const wFamily = 0.60 * (balance / 100);
  const rows = MAJORS.map((m) => {
    const subjectScore = normalize(m, "sj", state.subjects, SUBJ, 100);
    const interestScore = normalize(m, "ri", state.riasec, RIASEC, 5);
    const famScore = familyFit(m, state.parentFields);
    const outlookScore = OUTLOOK_SCORE[m.o];
    const final = wSubject * subjectScore + wOutlook * outlookScore + wInterest * interestScore + wFamily * famScore;
    return { major: m, score: final, subjectScore, interestScore, famScore, outlookScore };
  });
  rows.sort((a, b) => b.score - a.score);
  return rows;
}

function initialState() {
  const subjects = {};
  SUBJ.forEach((s) => { subjects[s] = 70; });
  const riasec = {};
  RIASEC.forEach((r) => { riasec[r] = 3; });
  return {
    lang: "en",
    screen: "hero",
    step: 0,
    profile: { name: "", grade: 0, country: "" },
    subjects,
    riasec,
    parentFields: {},
    familyWeight: 50,
    resultsBalance: 50
  };
}

function Topbar({ t, lang, onLang }) {
  const { user, login } = useAuth();
  const [showPhone, setShowPhone] = useState(false);
  const [showChoice, setShowChoice] = useState(false);
  return (
    <div className="topbar">
      <Link to="/" className="brandmark" style={{ textDecoration: "none" }}>
        <BrandmarkSVG /><span>{t.brand}</span>
      </Link>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className="langpicker">
          <select value={lang} onChange={(e) => onLang(e.target.value)} aria-label={t.langLabel}>
            {LANGS.map((l) => <option key={l[0]} value={l[0]}>{l[1]}</option>)}
          </select>
        </div>
        {user ? (
          <Link to="/dashboard" className="btn-secondary" style={{ textDecoration: "none", padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <LayoutDashboard size={14} /> Dashboard
          </Link>
        ) : (
          <button className="btn-secondary" style={{ padding: "8px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setShowChoice(true)}>
            <LogIn size={14} /> Sign in
          </button>
        )}
      </div>

      {showChoice && !user && (
        <div onClick={() => setShowChoice(false)} style={{ position: "fixed", inset: 0, background: "rgba(11,24,48,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 55 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 400, margin: 0, borderColor: "var(--brass)" }}>
            <div className="hintline" style={{ margin: 0, marginBottom: 8 }}>SIGN IN</div>
            <h2>How would you like to sign in?</h2>
            <p style={{ opacity: 0.8, fontSize: 14 }}>Pick whichever is easier — you can switch later.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
              <button className="btn-primary" onClick={() => { setShowChoice(false); login(); }} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <LogIn size={15} /> Continue with Google
              </button>
              <button className="btn-secondary" onClick={() => { setShowChoice(false); setShowPhone(true); }} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Smartphone size={15} /> Continue with phone
              </button>
            </div>
          </div>
        </div>
      )}
      {showPhone && <PhoneLogin onClose={() => setShowPhone(false)} onSuccess={() => setShowPhone(false)} />}
    </div>
  );
}

function StepNav({ t, step }) {
  const degs = ["000°", "090°", "180°", "270°", "360°"];
  return (
    <div className="stepnav">
      {[0,1,2,3,4].map((i) => {
        let cls = "stepitem";
        if (i === step) cls += " active";
        else if (i < step) cls += " done";
        return (
          <div key={i} className={cls}>
            <div className="deg">{degs[i]}</div>
            <div className="lbl">{t.stepTitles[i]}</div>
          </div>
        );
      })}
    </div>
  );
}

function Hero({ t, onStart }) {
  return (
    <div className="wrap">
      <div className="hero">
        <div className="hero-grid">
          <div>
            <div className="kicker">{t.stepTitles[4]} &middot; MVP</div>
            <h1>{t.tagline}</h1>
            <p className="lede">{t.heroBody}</p>
            <div className="cta-row">
              <button className="btn-primary" onClick={onStart}>{t.startBtn}</button>
            </div>
            <div className="disclaimer">{t.heroDisclaimer}</div>
          </div>
          <div className="hero-compass">
            <CompassSVG balance={50} leftLabel={t.familyWeightLeft} rightLabel={t.familyWeightRight} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileStep({ t, state, setState }) {
  return (
    <div className="card">
      <h2>{t.profileTitle}</h2>
      <div className="sub">{t.profileSubtitle}</div>
      <div className="field">
        <label>{t.nameLabel}</label>
        <input type="text" placeholder={t.namePlaceholder} value={state.profile.name}
          onChange={(e) => setState({ ...state, profile: { ...state.profile, name: e.target.value } })} />
      </div>
      <div className="field">
        <label>{t.gradeLabel}</label>
        <select value={state.profile.grade} onChange={(e) => setState({ ...state, profile: { ...state.profile, grade: parseInt(e.target.value, 10) } })}>
          {t.gradeOptions.map((g, i) => <option key={i} value={i}>{g}</option>)}
        </select>
      </div>
      <div className="field">
        <label>{t.countryLabel}</label>
        <input type="text" placeholder={t.countryPlaceholder} value={state.profile.country}
          onChange={(e) => setState({ ...state, profile: { ...state.profile, country: e.target.value } })} />
      </div>
    </div>
  );
}

function AcademicsStep({ t, state, setState }) {
  return (
    <div className="card">
      <h2>{t.academicsTitle}</h2>
      <div className="sub">{t.academicsSubtitle}</div>
      <div className="hintline">{t.academicsHint}</div>
      {SUBJ.map((s, i) => (
        <div key={s} className="slider-row">
          <div className="top">
            <span className="name">{t.subj[i]}</span>
            <span className="val">{state.subjects[s]}</span>
          </div>
          <input type="range" min="0" max="100" step="1" value={state.subjects[s]}
            onChange={(e) => setState({ ...state, subjects: { ...state.subjects, [s]: parseInt(e.target.value, 10) } })} />
        </div>
      ))}
    </div>
  );
}

function InterestsStep({ t, state, setState }) {
  return (
    <div className="card">
      <h2>{t.interestsTitle}</h2>
      <div className="sub">{t.interestsSubtitle}</div>
      <div className="hintline">{t.interestsHint}</div>
      {RIASEC.map((r, i) => (
        <div key={r} className="slider-row">
          <div className="top">
            <span className="name">{t.riasecName[i]}</span>
            <span className="val">{state.riasec[r]} / 5</span>
          </div>
          <div className="desc">{t.riasecDesc[i]}</div>
          <input type="range" min="1" max="5" step="1" value={state.riasec[r]}
            onChange={(e) => setState({ ...state, riasec: { ...state.riasec, [r]: parseInt(e.target.value, 10) } })} />
        </div>
      ))}
    </div>
  );
}

function FamilyStep({ t, state, setState }) {
  const toggle = (slug, checked) => {
    let next = { ...state.parentFields };
    if (slug === "undecided" && checked) {
      next = { undecided: true };
    } else if (checked) {
      delete next.undecided;
      next[slug] = true;
    } else {
      delete next[slug];
    }
    setState({ ...state, parentFields: next });
  };
  return (
    <div className="card">
      <h2>{t.familyTitle}</h2>
      <div className="sub">{t.familySubtitle}</div>
      <div className="hintline">{t.familyHint}</div>
      <div className="chipgrid">
        {PARENTF.map((p, i) => {
          const checked = !!state.parentFields[p];
          return (
            <label key={p} className={"chip" + (checked ? " checked" : "")}>
              <input type="checkbox" checked={checked} onChange={(e) => toggle(p, e.target.checked)} />
              {t.parent[i]}
            </label>
          );
        })}
      </div>
      <div className="balance-block" style={{ marginTop: 20 }}>
        <label className="balance-label">{t.familyWeightLabel}</label>
        <input type="range" min="0" max="100" step="1" value={state.familyWeight}
          onChange={(e) => setState({ ...state, familyWeight: parseInt(e.target.value, 10) })} />
        <div className="balance-ends"><span>{t.familyWeightLeft}</span><span>{t.familyWeightRight}</span></div>
      </div>
    </div>
  );
}

function ResultsScreen({ t, state, setState }) {
  const balance = state.resultsBalance;
  const rows = useMemo(() => computeResults(state, balance).slice(0, 5), [state, balance]);
  const topInterest = useMemo(() => computeResults(state, 0)[0], [state]);
  const topFamily = useMemo(() => computeResults(state, 100)[0], [state]);
  const diverge = topInterest.major.s !== topFamily.major.s;
  const wobble = diverge ? 26 : 0;
  const hasFamily = Object.keys(state.parentFields).some((k) => state.parentFields[k]);
  const [saveState, setSaveState] = useState({ status: "idle", shareId: null, error: null });
  const [copied, setCopied] = useState(false);

  const shareUrl = saveState.shareId ? `${window.location.origin}/r/${saveState.shareId}` : "";

  const save = async () => {
    setSaveState({ status: "saving", shareId: null, error: null });
    try {
      const payload = {
        lang: state.lang,
        profile: state.profile,
        subjects: state.subjects,
        riasec: state.riasec,
        parent_fields: state.parentFields,
        family_weight: state.familyWeight,
        results_balance: balance,
        diverges: diverge,
        top_majors: rows.map((r) => ({ slug: r.major.s, score: r.score })),
      };
      const res = await api.createRun(payload);
      setSaveState({ status: "saved", shareId: res.share_id, error: null });
    } catch (e) {
      setSaveState({ status: "idle", shareId: null, error: e.data?.detail || "Save failed" });
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { /* noop */ }
  };

  let conflictBlock;
  if (!diverge) {
    const mi2 = MAJORS.indexOf(topInterest.major);
    const body = t.conflictBodyAligned.replace("{major}", t.major[mi2]);
    conflictBlock = (
      <div className="conflictbox">
        <h3>{t.conflictTitleAligned}</h3>
        <p>{body}</p>
      </div>
    );
  } else {
    const miI = MAJORS.indexOf(topInterest.major);
    const miF = MAJORS.indexOf(topFamily.major);
    const body2 = t.conflictBodyDivergent.replace("{interestMajor}", t.major[miI]).replace("{familyMajor}", t.major[miF]);
    conflictBlock = (
      <div className="conflictbox">
        <h3>{t.conflictTitleDivergent}</h3>
        <p>{body2}</p>
      </div>
    );
  }

  const resetAll = () => {
    const fresh = initialState();
    fresh.lang = state.lang;
    setState(fresh);
  };

  return (
    <div className="wrap">
      <div className="card">
        <h2>{t.resultsTitle}</h2>
        <div className="sub">{t.resultsSubtitle}</div>
        {!hasFamily && <div className="hintline">{t.noFamilyNote}</div>}
        <div className="compass-live">
          <CompassSVG balance={balance} wobble={wobble} leftLabel={t.familyWeightLeft} rightLabel={t.familyWeightRight} />
        </div>
        <div className="balance-block">
          <label className="balance-label">{t.resultsAdjustLabel}</label>
          <input type="range" min="0" max="100" step="1" value={balance}
            onChange={(e) => setState({ ...state, resultsBalance: parseInt(e.target.value, 10) })} />
          <div className="balance-ends"><span>{t.familyWeightLeft}</span><span>{t.familyWeightRight}</span></div>
        </div>
      </div>

      {rows.map((row, idx) => {
        const mi = MAJORS.indexOf(row.major);
        const name = t.major[mi];
        const pct = Math.round(row.score * 100);
        return (
          <div key={mi} className="resultcard">
            <div className="rank">{idx + 1}</div>
            <div className="headrow">
              <h3>{name}</h3>
              <div className="matchpct">{pct}%</div>
            </div>
            <div className="badgerow">
              <span className="badge">{t.subjectAlignLabel}: {Math.round(row.subjectScore * 100)}%</span>
              <span className="badge">{t.interestAlignLabel}: {Math.round(row.interestScore * 100)}%</span>
              <span className="badge">{t.familyAlignLabel}: {Math.round(row.famScore * 100)}%</span>
              <span className="badge">{t.outlookLabel}: {t.outlook[row.major.o]}</span>
            </div>
            <div className="careerlabel">{t.resultsCareersLabel}</div>
            <div className="careerchips">
              {row.major.c.map((cs) => {
                const ci = CAREER_SLUGS.indexOf(cs);
                return <span key={cs} className="careerchip">{t.career[ci]}</span>;
              })}
            </div>
          </div>
        );
      })}

      {conflictBlock}
      <div className="disclaimerbox">{t.resultsDisclaimer}</div>

      <div className="card" style={{ marginTop: 20, borderColor: saveState.shareId ? "var(--brass)" : "var(--line)" }}>
        {!saveState.shareId ? (
          <>
            <h2 style={{ fontSize: 22 }}><Share2 size={18} style={{ verticalAlign: "middle", marginRight: 8, color: "var(--brass-bright)" }} />Save & share this bearing</h2>
            <div className="sub">Generate a link you can send to a parent, mentor or counselor. Free and anonymous — no account required.</div>
            {saveState.error && <p style={{ color: "var(--coral)", fontSize: 13 }}>{saveState.error}</p>}
            <button className="btn-primary" disabled={saveState.status === "saving"} onClick={save}>
              {saveState.status === "saving" ? "Generating link…" : "Create share link"}
            </button>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: 22 }}><Check size={18} style={{ verticalAlign: "middle", marginRight: 8, color: "var(--brass-bright)" }} />Share link ready</h2>
            <div className="sub">Anyone with this link can view your bearing. It stays live indefinitely.</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", background: "var(--navy)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px", flexWrap: "wrap" }}>
              <code style={{ flex: 1, minWidth: 200, fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--brass-bright)", wordBreak: "break-all" }}>{shareUrl}</code>
              <button className="btn-secondary" style={{ padding: "8px 14px", fontSize: 13 }} onClick={copy}>
                {copied ? <><Check size={13} style={{ verticalAlign: "middle", marginRight: 5 }} />Copied</> : <><Copy size={13} style={{ verticalAlign: "middle", marginRight: 5 }} />Copy</>}
              </button>
            </div>
          </>
        )}
      </div>

      <div className="navrow" style={{ margin: "24px 0 10px" }}>
        <button className="btn-secondary" onClick={resetAll}>{t.startOverBtn}</button>
        <button className="btn-primary" onClick={() => window.print()}>{t.printBtn}</button>
      </div>
    </div>
  );
}

function Wizard({ t, state, setState }) {
  const isLast = state.step === 3;
  const stepEls = [
    <ProfileStep key="0" t={t} state={state} setState={setState} />,
    <AcademicsStep key="1" t={t} state={state} setState={setState} />,
    <InterestsStep key="2" t={t} state={state} setState={setState} />,
    <FamilyStep key="3" t={t} state={state} setState={setState} />,
  ];
  return (
    <div className="wrap">
      <StepNav t={t} step={state.step} />
      {stepEls[state.step]}
      <div className="navrow">
        {state.step > 0
          ? <button className="btn-secondary" onClick={() => setState({ ...state, step: Math.max(0, state.step - 1) })}>{t.backBtn}</button>
          : <span />}
        {isLast
          ? <button className="btn-primary" onClick={() => setState({ ...state, resultsBalance: state.familyWeight, screen: "results" })}>{t.seeResultsBtn}</button>
          : <button className="btn-primary" onClick={() => setState({ ...state, step: Math.min(3, state.step + 1) })}>{t.nextBtn}</button>}
      </div>
    </div>
  );
}

export default function Bearing() {
  const [state, setState] = useState(initialState());
  const t = I18N[state.lang];

  useEffect(() => {
    document.documentElement.lang = state.lang;
    document.documentElement.dir = t.dir;
    document.documentElement.classList.toggle("lang-ar", state.lang === "ar");
    document.documentElement.classList.toggle("lang-zh", state.lang === "zh");
  }, [state.lang, t.dir]);

  const setLang = (lang) => setState({ ...state, lang });

  return (
    <div className="App">
      <Topbar t={t} lang={state.lang} onLang={setLang} />
      {state.screen === "hero" && <Hero t={t} onStart={() => setState({ ...state, screen: "wizard", step: 0 })} />}
      {state.screen === "wizard" && <Wizard t={t} state={state} setState={setState} />}
      {state.screen === "results" && <ResultsScreen t={t} state={state} setState={setState} />}
      <div className="footer-note">{t.footerNote}</div>
    </div>
  );
}
